/**
 * Concierge-specific settings
 * 
 * @example
 * window.brandclubSettings = {
 *   concierge: {
 *     alignment: 'right',
 *     horizontal_padding: 24,
 *     vertical_padding: 24,
 *     z_index: 1400,
 *     hide_default_launcher: false,
 *   }
 * };
 */
export interface ConciergeSettings {
  /** Position of the widget - 'left' or 'right' (default: 'right') */
  alignment?: 'left' | 'right';

  /** Horizontal padding from the edge in pixels (default: 24) */
  horizontal_padding?: number;

  /** Vertical padding from the bottom in pixels (default: 24) */
  vertical_padding?: number;

  /** Z-index of the widget and button (default: 6000; host values below the default are floored to it) */
  z_index?: number;

  /** Hide the default floating launcher button (default: false) */
  hide_default_launcher?: boolean;

  /** Custom icon URL for the launcher button */
  custom_launcher_icon?: string;
}

/**
 * Root Brandclub settings object
 * 
 * @example
 * window.brandclubSettings = {
 *   concierge: {
 *     alignment: 'left',
 *     horizontal_padding: 32,
 *   },
 *   // Future: other Brandclub settings can be added here
 * };
 */
export interface BrandclubSettings {
  /** Concierge widget settings */
  concierge?: ConciergeSettings;
}

// Default Concierge settings
export const DEFAULT_CONCIERGE_SETTINGS: Required<ConciergeSettings> = {
  alignment: 'right',
  horizontal_padding: 24,
  vertical_padding: 24,
  z_index: 6000,
  hide_default_launcher: false,
  custom_launcher_icon: '',
};

/**
 * Merge host-page settings over the defaults. Host themes that predate the
 * 6000 default carry lower `z_index` values (e.g. 5001 pasted into Shopify
 * theme snippets to beat the old 1400 default) — those were always "raise us
 * above the page", so a host value below the default is floored to it rather
 * than allowed to drag the widget back down.
 */
export function mergeConciergeSettings(hostSettings?: ConciergeSettings | null): Required<ConciergeSettings> {
  const merged = {
    ...DEFAULT_CONCIERGE_SETTINGS,
    ...hostSettings,
  };
  merged.z_index = Math.max(merged.z_index, DEFAULT_CONCIERGE_SETTINGS.z_index);
  return merged;
}

// Extend Window interface
declare global {
  interface Window {
    brandclubSettings?: BrandclubSettings;
  }
}
